import { useEffect, useRef, useCallback, useState } from 'react';

import {
  Room,
  RoomEvent,
  Track,
  type LocalVideoTrack,
  type LocalAudioTrack,
  type RemoteVideoTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
  type Participant,
} from 'livekit-client';

import { fetchLivekitToken, shipClientLogs } from 'mastodon/actions/rooms';
import type { ApiRoomParticipantJSON } from 'mastodon/api_types/rooms';
import { useAppDispatch } from 'mastodon/store';

import {
  collectRoomAudioElements,
  createWakeLockController,
  recoverBackgroundedAudio,
  shouldRestoreMicrophone,
  wakeLockRequester,
} from '../utils/background_audio';
import {
  applyMicGain,
  clampMicGainPercent,
  MIC_GAIN_DEFAULT_PERCENT,
  releaseMicGain,
} from '../utils/mic_gain';
import type { MicGainInstallation, MicGainTrack } from '../utils/mic_gain';
import { micGainLogLine } from '../utils/mic_gain_log';
import {
  applyOutputVolume,
  elementVolumeIsHonoured,
  releaseGainGraphs,
} from '../utils/output_volume';
import type { OutputGainGraphs } from '../utils/output_volume';

/**
 * A new AudioContext, or null where the browser has none. Safari still needs
 * the prefixed constructor, which is exactly the browser the playback gain
 * path (ticket 53) exists for.
 */
const createAudioContext = (): AudioContext | null => {
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctx) return null;
  try {
    return new Ctx();
  } catch {
    return null;
  }
};

export interface RemoteVideoEntry {
  identity: string;
  source: 'camera' | 'screen_share';
  track: RemoteVideoTrack;
  /**
   * Ticket 113: what the stage caps the received layer through. Optional so
   * entries built by older tests (and anything without one) simply get no
   * quality control.
   */
  publication?: RemoteTrackPublication;
}

export interface UseRoomAudioResult {
  isMuted: boolean;
  forceMuted: boolean;
  toggleMute: () => void;
  speakingPeers: Set<string>;
  // Is the LOCAL participant being heard right now? Ticket 69 uses this as the
  // host's presence signal, so it is deliberately derived from two independent
  // sources (see the connect effect): a silent false here would report a host
  // who is talking as away, and hand their co-hosts an End Room button.
  isLocalSpeaking: boolean;
  cleanup: () => void;
  getLocalStream: () => Promise<MediaStream>;
  // `exceptIdentity` leaves one participant out — the clip recorder uses it to
  // skip our own OBS ingest (ticket 112).
  getRemoteStreams: (exceptIdentity?: string) => MediaStream[];
  isCameraOn: boolean;
  isScreenSharing: boolean;
  toggleCamera: () => void;
  toggleScreenShare: () => void;
  // Publish-side mix: level of shared computer/tab audio relative to the mic
  // (100 = unchanged reference). Only meaningful while screen-sharing audio.
  computerAudioVolume: number;
  setComputerAudioVolume: (value: number) => void;
  // Ticket 96, publish side: gain on OUR OWN microphone, so everybody else in
  // the room hears us louder. Percent, 50..300, 100 = published exactly as
  // captured with no Web Audio in the path at all. Distinct in every way from
  // the per-participant OUTPUT volumes, which are about how loud other people
  // are to us and are not touched by this.
  micGain: number;
  setMicGain: (value: number) => void;
  // Echo guard: a share that can carry system audio (monitor capture, or any
  // audio-bearing share on browsers that report no displaySurface) would
  // recapture other participants' voices into the outgoing stream — display
  // capture has no echo cancellation, so viewers hear everyone twice. While
  // such a share is live, remote playback is muted locally on the sharer's
  // machine unless they explicitly override.
  echoRisk: boolean;
  echoMuted: boolean;
  toggleEchoMute: () => void;
  // Autoplay-block recovery: browsers refuse audible play() without a user
  // gesture. A viewer who lands in the room with no qualifying interaction
  // (announcement deep link, restored tab, strict autoplay settings) gets
  // every remote audio element stuck paused while video keeps rendering —
  // "I can see them but not hear them". audioBlocked surfaces that state;
  // resumeAudio must be called from a click handler (transient activation)
  // and replays everything via room.startAudio().
  audioBlocked: boolean;
  resumeAudio: () => void;
  // Master output volume for ALL room audio (0-100, persisted). Multiplies
  // with per-participant volume; 0 is an implied mute so a stream can play
  // in the background.
  masterVolume: number;
  setMasterVolume: (value: number) => void;
  localCameraTrack: LocalVideoTrack | null;
  localScreenTrack: LocalVideoTrack | null;
  remoteVideoTracks: Map<string, RemoteVideoEntry>;
  // Ticket 112: whether the SFU sends us the video of our OWN OBS ingest. Off
  // by default: a streamer's page used to decode their own 1080p stream while
  // OBS encoded it on the same machine. The stage offers a button to turn it
  // on for a preview.
  selfObsPreview: boolean;
  setSelfObsPreview: (on: boolean) => void;
  /** Ticket 113: a line for the room client log (the stage's quality control). */
  logClient: (msg: string) => void;
}

// Exported for tests.
export function videoSourceOf(
  pub: RemoteTrackPublication,
): 'camera' | 'screen_share' | null {
  if (pub.source === Track.Source.Camera) return 'camera';
  if (pub.source === Track.Source.ScreenShare) return 'screen_share';
  // WHIP/OBS-ingested tracks arrive with source Unknown (raw WebRTC publish
  // carries no LiveKit source metadata) — render any other video track as a
  // camera tile rather than dropping it.
  if (pub.kind === Track.Kind.Video) return 'camera';
  return null;
}

/**
 * Extract a usable MediaStream from a LiveKit track.
 */
function getStreamFromTrack(track: {
  mediaStreamTrack: MediaStreamTrack;
  mediaStream?: MediaStream;
}): MediaStream {
  if (track.mediaStream) return track.mediaStream;
  return new MediaStream([track.mediaStreamTrack]);
}

export function useRoomAudio(
  roomId: string | null,
  participants: ApiRoomParticipantJSON[],
  myAccountId: string,
  onRemoteStreamAdded?: (stream: MediaStream) => void,
  onRemoteStreamRemoved?: (stream: MediaStream) => void,
  participantVolumes?: Record<string, number>,
): UseRoomAudioResult {
  const dispatch = useAppDispatch();
  const roomRef = useRef<Room | null>(null);
  // Mute preference is persisted (same pattern as master volume below): a
  // reload or leave+rejoin must come back with the mic in the state the user
  // left it — publishing hot after the UI showed 🔇 is a privacy bug.
  const [isMuted, setIsMuted] = useState(() => {
    try {
      return localStorage.getItem('mastodon_room_mic_muted') === '1';
    } catch {
      return false;
    }
  });
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;
  const [speakingPeers, setSpeakingPeers] = useState<Set<string>>(new Set());
  const [selfObsPreview, setSelfObsPreviewState] = useState(false);
  const selfObsPreviewRef = useRef(false);
  const selfObsPubRef = useRef<RemoteTrackPublication | null>(null);
  const [isLocalSpeaking, setIsLocalSpeaking] = useState(false);
  // The two sources are OR'd rather than picked between: ActiveSpeakersChanged
  // is server-computed and may or may not carry the local identity depending on
  // the SFU's speaker update, while IsSpeakingChanged is computed locally from
  // the published track. Either one alone is enough to prove voice.
  const localSpeakingSourcesRef = useRef({ active: false, own: false });
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [localCameraTrack, setLocalCameraTrack] =
    useState<LocalVideoTrack | null>(null);
  const [localScreenTrack, setLocalScreenTrack] =
    useState<LocalVideoTrack | null>(null);
  const [echoRisk, setEchoRisk] = useState(false);
  const [echoMuteOverride, setEchoMuteOverride] = useState(false);
  const echoSuppressRef = useRef(false);
  const [audioBlocked, setAudioBlocked] = useState(false);

  // Master output volume (0-100), persisted so "background listening" level
  // survives rejoins. Multiplied with per-participant volume when applied.
  const [masterVolume, setMasterVolumeState] = useState(() => {
    try {
      const stored = localStorage.getItem('mastodon_room_master_volume');
      const parsed = stored === null ? NaN : Number(stored);
      if (Number.isFinite(parsed)) return Math.max(0, Math.min(100, parsed));
    } catch {
      /* storage unavailable */
    }
    return 100;
  });
  const setMasterVolume = useCallback((value: number) => {
    const clamped = Math.max(0, Math.min(100, Math.round(value)));
    setMasterVolumeState(clamped);
    try {
      localStorage.setItem('mastodon_room_master_volume', String(clamped));
    } catch {
      /* storage unavailable */
    }
  }, []);
  const [remoteVideoTracks, setRemoteVideoTracks] = useState<
    Map<string, RemoteVideoEntry>
  >(new Map());
  const myAccountIdRef = useRef(myAccountId);
  myAccountIdRef.current = myAccountId;

  // Publish-side computer-audio mix. Default 100 = raw captured track published
  // untouched (no WebAudio in the path). A GainNode is spliced into the
  // screen-share audio track lazily, only once the user moves the slider off
  // 100 — so the common case never risks the capture pipeline.
  const [computerAudioVolume, setComputerAudioVolumeState] = useState(100);
  const computerAudioVolumeRef = useRef(100);
  computerAudioVolumeRef.current = computerAudioVolume;
  const audioMixRef = useRef<{ ctx: AudioContext; gain: GainNode } | null>(
    null,
  );

  // Ticket 96, publish side: gain on our OWN microphone. Persisted, because
  // unlike the per-participant output volumes (which are about other people, in
  // one particular room) this is a property of YOUR microphone and is the same
  // wherever you go — having to re-set a quiet headset in every room would be
  // the annoying behaviour. It is not a silent setting either: the control
  // reports its own level while closed.
  const [micGain, setMicGainState] = useState(() => {
    try {
      const stored = localStorage.getItem('mastodon_room_mic_gain');
      if (stored !== null) return clampMicGainPercent(Number(stored));
    } catch {
      /* storage unavailable */
    }
    return MIC_GAIN_DEFAULT_PERCENT;
  });
  const micGainRef = useRef(MIC_GAIN_DEFAULT_PERCENT);
  micGainRef.current = micGain;
  const micGainInstallRef = useRef<MicGainInstallation | null>(null);
  const micGainLoggedRef = useRef<Map<string, number>>(new Map());
  // The connect effect must be able to re-apply a persisted boost, but it must
  // not re-run when the boost changes — reconnecting to LiveKit because someone
  // moved a slider would be a spectacular own goal. Hence a ref to the latest
  // implementation rather than a dependency on it.
  const syncMicGainRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // Ticket 53 item 1, PLAYBACK side (the mix above is the publish side).
  // One gain graph per remote playback element, built only where the element's
  // own `volume` property is inert — i.e. iOS Safari. Probed once per mount:
  // the answer cannot change under a live page, and probing per slider move
  // would allocate an element on every drag frame.
  const elementVolumeHonouredRef = useRef<boolean | null>(null);
  elementVolumeHonouredRef.current ??= elementVolumeIsHonoured();
  const outputGraphsRef = useRef<OutputGainGraphs>(new Map());

  // Log buffer for shipping client logs to server
  const logBufferRef = useRef<string[]>([]);
  const roomIdRef = useRef(roomId);
  roomIdRef.current = roomId;

  const flushLogs = useCallback(() => {
    const rid = roomIdRef.current;
    if (rid && logBufferRef.current.length > 0) {
      const entries = logBufferRef.current.splice(0);
      void dispatch(shipClientLogs(rid, entries)).then((shipped) => {
        // A failed ship usually means the origin is struggling — the exact
        // moment these logs matter. Re-queue for the next flush, bounded so
        // an extended outage can't grow the buffer without limit.
        if (!shipped) {
          logBufferRef.current = [...entries, ...logBufferRef.current].slice(
            -200,
          );
        }
      });
    }
  }, [dispatch]);

  const log = useCallback(
    (msg: string) => {
      console.log(msg);
      logBufferRef.current.push(msg);
      if (
        msg.includes('FAIL') ||
        msg.includes('failed') ||
        msg.includes('disconnected') ||
        msg.includes('ICE') ||
        msg.includes('candidate') ||
        msg.includes('BLOCKED') ||
        msg.includes('STALLED') ||
        msg.includes('AUDIO_ELEMENT_ERROR')
      ) {
        flushLogs();
      }
    },
    [flushLogs],
  );

  useEffect(() => {
    if (!roomId) return;
    const timer = setInterval(flushLogs, 3000);
    return () => {
      clearInterval(timer);
      flushLogs();
    };
  }, [roomId, flushLogs]);

  const remoteStreamsRef = useRef<Map<string, MediaStream>>(new Map());
  const firstAudioLoggedRef = useRef<Set<string>>(new Set());

  const onRemoteStreamAddedRef = useRef(onRemoteStreamAdded);
  const onRemoteStreamRemovedRef = useRef(onRemoteStreamRemoved);
  onRemoteStreamAddedRef.current = onRemoteStreamAdded;
  onRemoteStreamRemovedRef.current = onRemoteStreamRemoved;

  const myParticipant = participants.find((p) => p.account.id === myAccountId);
  const myRole = myParticipant?.role;
  const canPublishRef = useRef(
    myRole === 'host' || myRole === 'cohost' || myRole === 'speaker',
  );
  canPublishRef.current =
    myRole === 'host' || myRole === 'cohost' || myRole === 'speaker';
  const forceMuted = myParticipant?.force_muted ?? false;
  const forceMutedRef = useRef(forceMuted);
  forceMutedRef.current = forceMuted;

  // Connect to LiveKit room
  useEffect(() => {
    if (!roomId) return;

    log(
      `[Room] Initializing for room=${roomId} myRole=${myRole} canPublish=${canPublishRef.current}`,
    );

    // Workaround for LiveKit SDK v2.18 bug: when CompressionStream is available,
    // the SDK takes a code path that calls pcManager.updateConfiguration() before
    // pcManager is initialized (it's null). This causes ICE servers to never be applied
    // and SDP negotiation to never start. Disabling CompressionStream forces the SDK
    // to take the working code path that calls configure() first.
    const origCS = (window as any).CompressionStream;
    (window as any).CompressionStream = undefined;

    const room = new Room({
      adaptiveStream: false,
      dynacast: true,
      audioCaptureDefaults: { autoGainControl: true, noiseSuppression: true },
    });
    roomRef.current = room;

    room.on(RoomEvent.ConnectionStateChanged, (state: string) => {
      log(`[Room] ConnectionStateChanged: ${state}`);
    });

    room.on(RoomEvent.Disconnected, () =>
      log('[Room] Disconnected from LiveKit'),
    );
    room.on(RoomEvent.Reconnecting, () => log('[Room] Reconnecting...'));
    room.on(RoomEvent.Reconnected, () => log('[Room] Reconnected'));

    // Browsers refuse audible play() without user interaction. When that
    // happens every attached element rejects play() and sits paused — video
    // keeps rendering, so the user "sees but can't hear". LiveKit surfaces
    // it here; the UI offers a tap target whose click handler (transient
    // activation) calls startAudio() to replay everything.
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      const ok = room.canPlaybackAudio;
      setAudioBlocked(!ok);
      log(
        `[Room] AUDIO_PLAYBACK_STATUS: canPlayback=${ok}${ok ? '' : ' — browser BLOCKED audible autoplay; showing tap-to-listen prompt'}`,
      );
    });

    room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
      const ids = speakers.map((s) => s.identity);
      // Ticket 112: the SFU repeats this event while the same people go on
      // talking. A new Set every time re-rendered the whole room — chat
      // included — several times a second for no change at all.
      setSpeakingPeers((prev) =>
        prev.size === ids.length && ids.every((id) => prev.has(id))
          ? prev
          : new Set(ids),
      );
      localSpeakingSourcesRef.current.active = ids.includes(myAccountId);
      setIsLocalSpeaking(
        localSpeakingSourcesRef.current.active ||
          localSpeakingSourcesRef.current.own,
      );
      // Log full speaker list every time it changes (for debugging audio routing)
      if (ids.length > 0) {
        log(`[Room] ACTIVE_SPEAKERS: [${ids.join(', ')}]`);
      }
      for (const id of ids) {
        if (!firstAudioLoggedRef.current.has(id)) {
          firstAudioLoggedRef.current.add(id);
          log(`[Room] FIRST_AUDIO_DETECTED participant=${id}`);
        }
      }
    });

    // Second, independent source for the local speaking state (ticket 69).
    // LiveKit computes this one from our own published track, so it still works
    // if the SFU's active-speaker update never names us — and an hour of a
    // false "not speaking" hands this host's co-hosts an End Room button.
    //
    // The event is named as a string (it is ParticipantEvent.IsSpeakingChanged)
    // and the subscription is optional: every existing test in this feature
    // mocks livekit-client, and neither the enum nor localParticipant.on is
    // guaranteed to be there. A missing listener degrades to the source above.
    const localParticipant = room.localParticipant as unknown as {
      on?: (event: string, cb: (speaking: boolean) => void) => void;
    };

    localParticipant.on?.('isSpeakingChanged', (speaking: boolean) => {
      localSpeakingSourcesRef.current.own = speaking;
      setIsLocalSpeaking(speaking || localSpeakingSourcesRef.current.active);
    });

    room.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
      log(`[Room] ParticipantConnected: ${p.identity}`);
    });
    room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
      log(`[Room] ParticipantDisconnected: ${p.identity}`);
    });

    room.on(
      RoomEvent.TrackSubscribed,
      (track, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Video) {
          const source = videoSourceOf(_pub);
          if (!source) return;
          log(
            `[Room] VIDEO TrackSubscribed: participant=${participant.identity} source=${source} trackSid=${_pub.trackSid}`,
          );
          // Ticket 112: our own OBS ingest. Ask the SFU to stop sending its
          // video unless we asked for a preview — decoding it cost a 1080p
          // decode on the very machine OBS is encoding on. The track stays
          // subscribed, so the stage still knows OBS is live.
          if (participant.identity === `${myAccountIdRef.current}-obs`) {
            selfObsPubRef.current = _pub;
            if (!selfObsPreviewRef.current) {
              _pub.setEnabled(false);
              log(
                `[Room] SELF_OBS_VIDEO_PAUSED participant=${participant.identity}`,
              );
            }
          }
          setRemoteVideoTracks((prev) => {
            const next = new Map(prev);
            next.set(`${participant.identity}|${source}`, {
              identity: participant.identity,
              source,
              track: track as RemoteVideoTrack,
              publication: _pub,
            });
            return next;
          });
          return;
        }
        if (track.kind !== Track.Kind.Audio) return;
        log(
          `[Room] TrackSubscribed: participant=${participant.identity} trackSid=${_pub.trackSid} mime=${_pub.mimeType} subscribed=${_pub.isSubscribed}`,
        );
        const el = track.attach();
        // Screen-share audio gets its own element — reusing the mic element id
        // would collide (getElementById returns the first match, so per-source
        // lookup/volume control would silently target the wrong track).
        el.id =
          _pub.source === Track.Source.ScreenShareAudio
            ? `lk-audio-${participant.identity}-screen`
            : `lk-audio-${participant.identity}`;
        // Never play back our OWN OBS ingest audio. The ingest publishes as a
        // separate `<myAccountId>-obs` identity, so it looks remote — but a host
        // monitoring the room in a browser while OBS captures desktop audio
        // would loop it straight back into OBS (virtual feedback, headphones or
        // not). The video tile still shows; only the local audio is muted.
        if (participant.identity === `${myAccountIdRef.current}-obs`) {
          el.muted = true;
          log(
            `[Room] SELF_OBS_AUDIO_MUTED participant=${participant.identity}`,
          );
        } else if (echoSuppressRef.current) {
          // Echo guard active — audio arriving mid-share joins the muted set.
          el.muted = true;
          log(`[Room] ECHO_GUARD_MUTED participant=${participant.identity}`);
        }
        document.body.appendChild(el);
        el.addEventListener('error', () => {
          log(
            `[Room] AUDIO_ELEMENT_ERROR participant=${participant.identity} code=${el.error?.code ?? 'unknown'} message=${el.error?.message ?? ''}`,
          );
        });
        log(
          `[Room] AUDIO_ELEMENT: participant=${participant.identity} paused=${el.paused} muted=${el.muted} volume=${el.volume} srcObject=${!!el.srcObject} readyState=${el.readyState}`,
        );
        const stream = getStreamFromTrack(track);
        remoteStreamsRef.current.set(participant.identity, stream);
        onRemoteStreamAddedRef.current?.(stream);
      },
    );

    room.on(
      RoomEvent.TrackUnsubscribed,
      (track, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Video) {
          const source = videoSourceOf(_pub);
          if (!source) return;
          log(
            `[Room] VIDEO TrackUnsubscribed: participant=${participant.identity} source=${source}`,
          );
          if (selfObsPubRef.current === _pub) selfObsPubRef.current = null;
          setRemoteVideoTracks((prev) => {
            const next = new Map(prev);
            next.delete(`${participant.identity}|${source}`);
            return next;
          });
          return;
        }
        if (track.kind !== Track.Kind.Audio) return;
        log(`[Room] TrackUnsubscribed: participant=${participant.identity}`);
        track.detach().forEach((el) => el.remove());
        const stream = remoteStreamsRef.current.get(participant.identity);
        if (stream) {
          onRemoteStreamRemovedRef.current?.(stream);
          remoteStreamsRef.current.delete(participant.identity);
        }
      },
    );

    // Detect server-side force-mute of our local mic. When the host calls
    // mute_participant via the API, LiveKit emits TrackMuted on our local
    // participant. We update local mute state so the UI reflects it.
    room.on(RoomEvent.TrackMuted, (pub, participant: Participant) => {
      // A muted remote camera stops sending frames — drop the tile rather than
      // showing a frozen frame. It comes back on TrackUnmuted.
      if (
        pub.kind === Track.Kind.Video &&
        participant.identity !== room.localParticipant.identity
      ) {
        const source = videoSourceOf(pub as RemoteTrackPublication);
        if (source) {
          setRemoteVideoTracks((prev) => {
            const next = new Map(prev);
            next.delete(`${participant.identity}|${source}`);
            return next;
          });
        }
        return;
      }
      if (pub.kind !== Track.Kind.Audio) return;
      if (participant.identity === room.localParticipant.identity) {
        log('[Room] LOCAL_TRACK_MUTED (server force-mute or local mute)');
        setIsMuted(true);
      }
    });

    room.on(RoomEvent.TrackUnmuted, (pub, participant: Participant) => {
      if (
        pub.kind === Track.Kind.Video &&
        participant.identity !== room.localParticipant.identity
      ) {
        const source = videoSourceOf(pub as RemoteTrackPublication);
        const track = (pub as RemoteTrackPublication).track;
        if (source && track) {
          setRemoteVideoTracks((prev) => {
            const next = new Map(prev);
            next.set(`${participant.identity}|${source}`, {
              identity: participant.identity,
              source,
              track: track as RemoteVideoTrack,
              publication: pub as RemoteTrackPublication,
            });
            return next;
          });
        }
        return;
      }
      if (pub.kind !== Track.Kind.Audio) return;
      if (participant.identity === room.localParticipant.identity) {
        log('[Room] LOCAL_TRACK_UNMUTED');
        setIsMuted(false);
      }
    });

    // Screen sharing can be stopped from the browser's own "Stop sharing"
    // chrome, which unpublishes the track without going through our toggle.
    room.on(RoomEvent.LocalTrackUnpublished, (pub) => {
      if (pub.source === Track.Source.ScreenShare) {
        log('[Room] Local screen share unpublished');
        setIsScreenSharing(false);
        setLocalScreenTrack(null);
        setEchoRisk(false);
        setEchoMuteOverride(false);
      } else if (pub.source === Track.Source.Camera) {
        log('[Room] Local camera unpublished');
        setIsCameraOn(false);
        setLocalCameraTrack(null);
      }
    });

    // Rewrite TURN port: LiveKit advertises port 5349 but nginx SNI proxy
    // routes lk.nofilter.social:443 → LiveKit:5349. Rewrite URLs so the
    // browser connects to port 443.
    room.on(RoomEvent.SignalConnected, () => {
      try {
        const engine = (room as any).engine;
        const joinRes = engine?.latestJoinResponse;
        if (joinRes?.iceServers?.length > 0) {
          const iceServers = joinRes.iceServers.map((s: any) => ({
            urls: (s.urls || []).map((u: string) => u.replace(':5349', ':443')),
            username: s.username || undefined,
            credential: s.credential || undefined,
          }));
          log(
            `[Room] ICE servers (port-rewritten): ${JSON.stringify(iceServers).slice(0, 300)}`,
          );

          const pcMgr = engine?.pcManager;
          const pcs = [pcMgr?.publisher?._pc, pcMgr?.subscriber?._pc].filter(
            Boolean,
          );
          for (const pc of pcs) {
            try {
              pc.setConfiguration({ iceServers, iceTransportPolicy: 'relay' });
              log('[Room] ICE: Applied rewritten TURN config');
            } catch (e) {
              log(`[Room] ICE setConfiguration failed: ${e}`);
            }
          }
        } else {
          log('[Room] No ICE servers in join response');
        }
      } catch (e) {
        log(`[Room] SignalConnected handler error: ${e}`);
      }
    });

    // Connect — use a two-phase approach:
    // 1. First do a "dry" signaling connection to get the TURN servers from the Join Response
    // 2. Then connect for real with the TURN servers pre-configured in rtcConfig
    const connect = async () => {
      try {
        log(`[Room] Fetching LiveKit token for room=${roomId}...`);
        const { token, url } = await dispatch(fetchLivekitToken(roomId));
        log(`[Room] Got token, connecting to url=${url}`);

        // First connect: the SDK will get the join response with TURN servers
        // and apply them via updateConfiguration. If that doesn't work,
        // we'll extract them and reconnect.
        await room.connect(url, token);
        // Restore CompressionStream after connect
        if (origCS) (window as any).CompressionStream = origCS;
        log(
          `[Room] Connected! localParticipant=${room.localParticipant.identity} remoteParticipants=${room.remoteParticipants.size}`,
        );

        // Log PC state after successful connection — both publisher AND subscriber
        try {
          const engine = (room as any).engine;
          const pubPC = engine?.pcManager?.publisher?._pc;
          const subPC = engine?.pcManager?.subscriber?._pc;
          if (pubPC) {
            const cfg = pubPC.getConfiguration?.();
            log(
              `[Room] PUBLISHER PC: policy=${cfg?.iceTransportPolicy} servers=${cfg?.iceServers?.length} iceState=${pubPC.iceConnectionState} gatherState=${pubPC.iceGatheringState}`,
            );
          } else {
            log('[Room] PUBLISHER PC: not available');
          }
          if (subPC) {
            const cfg = subPC.getConfiguration?.();
            log(
              `[Room] SUBSCRIBER PC: policy=${cfg?.iceTransportPolicy} servers=${cfg?.iceServers?.length} iceState=${subPC.iceConnectionState} gatherState=${subPC.iceGatheringState}`,
            );
          } else {
            log('[Room] SUBSCRIBER PC: not available');
          }
        } catch {
          /* ignore */
        }

        if (canPublishRef.current) {
          // Honor the remembered preference — never force-unmute on join.
          log(`[Room] Enabling microphone (muted=${isMutedRef.current})...`);
          await room.localParticipant.setMicrophoneEnabled(!isMutedRef.current);
          // Log which audio device is actually being used
          try {
            const micPub = room.localParticipant.getTrackPublication(
              Track.Source.Microphone,
            );
            const micTrack = micPub?.track?.mediaStreamTrack;
            if (micTrack) {
              const settings = micTrack.getSettings();
              log(
                `[Room] MIC_DEVICE: label="${micTrack.label}" deviceId=${settings.deviceId?.slice(0, 12)}... sampleRate=${settings.sampleRate} channelCount=${settings.channelCount} enabled=${micTrack.enabled} muted=${micTrack.muted} readyState=${micTrack.readyState}`,
              );
            } else {
              log('[Room] MIC_DEVICE: track not available after enable');
            }
          } catch (e) {
            log(`[Room] MIC_DEVICE: error reading device info: ${e}`);
          }
          log('[Room] Microphone enabled');
          // Ticket 96: a persisted input boost has to be re-applied to the
          // freshly published track. Not left to LocalTrackPublished alone — the
          // listener is registered by a later effect, and a boost that depends
          // on effect ordering is a boost that silently does not happen.
          if (micGainRef.current !== MIC_GAIN_DEFAULT_PERCENT) {
            await syncMicGainRef.current();
          }
        } else {
          log('[Room] Skipping microphone (listener)');
        }

        // Nobody goes on camera automatically — not even the host. Privacy:
        // going live must never blast someone's image before they've chosen
        // a source from the stream-source menu ("Not streaming" default).
      } catch (err) {
        if (origCS) (window as any).CompressionStream = origCS;
        log(`[Room] LiveKit connection failed: ${String(err)}`);
      }
    };

    void connect();

    // Periodic RTC stats — log bytes sent/received per track every 10s to detect silent streams
    const statsInterval = setInterval(async () => {
      try {
        const engine = (room as any).engine;
        const pubPC: RTCPeerConnection | undefined =
          engine?.pcManager?.publisher?._pc;
        const subPC: RTCPeerConnection | undefined =
          engine?.pcManager?.subscriber?._pc;

        // Publisher stats (what we're sending)
        if (pubPC && pubPC.connectionState !== 'closed') {
          const stats = await pubPC.getStats();
          const outbound: string[] = [];
          stats.forEach((report: any) => {
            if (report.type === 'outbound-rtp' && report.kind === 'audio') {
              outbound.push(
                `ssrc=${report.ssrc} bytes=${report.bytesSent} packets=${report.packetsSent}`,
              );
            }
          });
          if (outbound.length > 0) {
            log(`[Room] STATS_PUB_AUDIO: ${outbound.join(' | ')}`);
          }
        }

        // Subscriber stats (what we're receiving)
        if (subPC && subPC.connectionState !== 'closed') {
          const stats = await subPC.getStats();
          const inbound: string[] = [];
          stats.forEach((report: any) => {
            if (report.type === 'inbound-rtp' && report.kind === 'audio') {
              inbound.push(
                `ssrc=${report.ssrc} bytes=${report.bytesReceived} packets=${report.packetsReceived} lost=${report.packetsLost}`,
              );
            }
          });
          if (inbound.length > 0) {
            log(`[Room] STATS_SUB_AUDIO: ${inbound.join(' | ')}`);
          }

          // Playback health: bytes arriving proves the SUBSCRIPTION works,
          // not that anyone can hear — an autoplay-blocked or errored
          // element sits paused while stats look perfect. Flag any attached
          // audio element that isn't actually playing.
          const stalled: string[] = [];
          for (const identity of remoteStreamsRef.current.keys()) {
            for (const suffix of ['', '-screen']) {
              const el = document.getElementById(
                `lk-audio-${identity}${suffix}`,
              ) as HTMLAudioElement | null;
              if (el?.paused) stalled.push(`${identity}${suffix}`);
            }
          }
          if (stalled.length > 0) {
            log(
              `[Room] AUDIO_ELEMENT_STALLED: [${stalled.join(', ')}] — attached but not playing (autoplay-blocked or media error)`,
            );
          }

          // Also log subscriber PC state periodically
          log(
            `[Room] SUBSCRIBER_STATE: ice=${subPC.iceConnectionState} conn=${subPC.connectionState} signaling=${subPC.signalingState}`,
          );
        }
      } catch {
        /* stats collection failed, ignore */
      }
    }, 10_000);

    return () => {
      clearInterval(statsInterval);
      log(`[Room] Cleanup: disconnecting from room=${roomId}`);
      for (const identity of remoteStreamsRef.current.keys()) {
        document.getElementById(`lk-audio-${identity}`)?.remove();
        document.getElementById(`lk-audio-${identity}-screen`)?.remove();
      }
      remoteStreamsRef.current.clear();
      firstAudioLoggedRef.current.clear();
      setRemoteVideoTracks(new Map());
      setLocalCameraTrack(null);
      setLocalScreenTrack(null);
      setIsCameraOn(false);
      setIsScreenSharing(false);
      setAudioBlocked(false);
      void room.disconnect();
      roomRef.current = null;
    };
  }, [roomId, dispatch, log]);

  // Handle role changes — enable/disable mic without disconnecting.
  // LiveKit updates permissions server-side; we just need to toggle the mic locally.
  const prevRoleRef = useRef<string | undefined | null>('__initial__');

  useEffect(() => {
    if (!roomId || !roomRef.current) return;
    if (prevRoleRef.current === '__initial__') {
      prevRoleRef.current = myRole;
      return;
    }
    if (prevRoleRef.current === myRole) return;
    const oldRole = prevRoleRef.current;
    prevRoleRef.current = myRole;
    log(`[Room] ROLE_CHANGE detected: ${oldRole} -> ${myRole}`);

    const room = roomRef.current;
    const canPublish =
      myRole === 'host' || myRole === 'cohost' || myRole === 'speaker';

    if (canPublish && (oldRole === 'listener' || !oldRole)) {
      // Promoted to speaker/host — enable mic
      log('[Room] Promoted! Enabling microphone...');
      void room.localParticipant
        .setMicrophoneEnabled(!isMuted)
        .then(() => {
          log('[Room] Microphone enabled after promotion');
        })
        .catch((err) => {
          log(`[Room] Failed to enable mic after promotion: ${err}`);
        });
    } else if (
      !canPublish &&
      (oldRole === 'speaker' || oldRole === 'cohost' || oldRole === 'host')
    ) {
      // Demoted to listener — disable mic
      log('[Room] Demoted to listener, disabling microphone');
      void room.localParticipant.setMicrophoneEnabled(false);
    }
  }, [roomId, myRole, isMuted, log]);

  // Re-publish microphone when host lifts a force-mute. When can_publish was
  // revoked, the LiveKit SDK unpublished our track, so an unmute on the
  // server has nothing to unmute — we have to re-enable the mic locally.
  const prevForceMutedRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (!roomId || !roomRef.current) return;
    const prev = prevForceMutedRef.current;
    prevForceMutedRef.current = forceMuted;
    if (prev === undefined) return;
    if (prev === true && forceMuted === false) {
      const room = roomRef.current;
      if (myRole !== 'speaker' && myRole !== 'cohost' && myRole !== 'host')
        return;
      log('[Room] Force-mute lifted — re-enabling microphone');
      setIsMuted(false);
      void room.localParticipant
        .setMicrophoneEnabled(true)
        .then(() => {
          log('[Room] Microphone re-enabled after unmute');
        })
        .catch((err) => {
          log(`[Room] Failed to re-enable mic after unmute: ${err}`);
        });
    }
  }, [roomId, forceMuted, myRole, log]);

  // Apply output volume: per-participant volume × master volume. Master at
  // 0 is the "background tab" mute; both are clamped to [0, 1].
  //
  // Ticket 53: routed through `applyOutputVolume` rather than
  // `track.setVolume` directly, because on iOS Safari the audio element's
  // `volume` property is read-only and every level here was silently
  // discarded — the slider, mute included, did nothing at all. Desktop takes
  // the identical `setVolume` path it always did; see utils/output_volume.ts.
  useEffect(() => {
    const room = roomRef.current;
    if (!room) return;
    const master = Math.max(0, Math.min(1, masterVolume / 100));
    const options = {
      honoursElementVolume: elementVolumeHonouredRef.current ?? true,
      graphs: outputGraphsRef.current,
      createContext: createAudioContext,
      log,
    };
    for (const remoteParticipant of room.remoteParticipants.values()) {
      const accountId = remoteParticipant.identity;
      const participant = participants.find((p) => p.account.id === accountId);
      for (const pub of remoteParticipant.audioTrackPublications.values()) {
        if (!pub.track) continue;
        const vol =
          participant?.role === 'listener'
            ? 0
            : Math.max(
                0,
                Math.min(1, (participantVolumes?.[accountId] ?? 100) / 100),
              ) * master;
        applyOutputVolume(pub.track, vol, options);
      }
    }
  }, [participantVolumes, participants, masterVolume, log]);

  const getLocalStream = useCallback(async () => {
    const room = roomRef.current;
    if (room) {
      const localPub = room.localParticipant.getTrackPublication(
        Track.Source.Microphone,
      );
      if (localPub?.track) return getStreamFromTrack(localPub.track);
    }
    return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  }, []);

  const getRemoteStreams = useCallback((exceptIdentity?: string) => {
    return Array.from(remoteStreamsRef.current.entries())
      .filter(([identity]) => identity !== exceptIdentity)
      .map(([, stream]) => stream);
  }, []);

  const setSelfObsPreview = useCallback(
    (on: boolean) => {
      selfObsPreviewRef.current = on;
      setSelfObsPreviewState(on);
      selfObsPubRef.current?.setEnabled(on);
      log(`[Room] SELF_OBS_PREVIEW ${on ? 'on' : 'off'}`);
    },
    [log],
  );

  // Must be called from a click/tap handler: startAudio() only succeeds
  // inside transient user activation, which is the whole point — it replays
  // every attached-but-blocked audio element under that activation.
  const resumeAudio = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    log('[Room] AUDIO_PLAYBACK_RESUME: user tapped enable-audio');
    room
      .startAudio()
      .then(() => {
        log('[Room] AUDIO_PLAYBACK_RESUME: startAudio() succeeded');
        setAudioBlocked(false);
      })
      .catch((err: unknown) => {
        log(`[Room] AUDIO_PLAYBACK_RESUME failed: ${String(err)}`);
      });
  }, [log]);

  const toggleMute = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    const newMuted = !isMuted;
    // Refuse to self-unmute when the host has force-muted us. LiveKit also
    // revokes canPublish server-side, but this prevents an inconsistent UI
    // flicker where the button toggles but the track stays muted.
    if (!newMuted && forceMutedRef.current) {
      log('[Room] toggleMute blocked: force-muted by host');
      return;
    }
    log(`[Room] toggleMute: ${isMuted} -> ${newMuted}`);
    setIsMuted(newMuted);
    try {
      localStorage.setItem('mastodon_room_mic_muted', newMuted ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
    void room.localParticipant.setMicrophoneEnabled(!newMuted);
  }, [isMuted, log]);

  const toggleCamera = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    const next = !isCameraOn;
    log(`[Room] toggleCamera: ${isCameraOn} -> ${next}`);
    room.localParticipant
      .setCameraEnabled(next)
      .then((pub) => {
        setIsCameraOn(next);
        setLocalCameraTrack(
          next
            ? ((pub?.track ??
                room.localParticipant.getTrackPublication(Track.Source.Camera)
                  ?.track ??
                null) as LocalVideoTrack | null)
            : null,
        );
      })
      .catch((err: unknown) => {
        log(`[Room] toggleCamera failed: ${String(err)}`);
      });
  }, [isCameraOn, log]);

  // Splice a WebAudio GainNode into the published screen-share audio track so
  // its level can be mixed against the mic. Lazy: called only when the user
  // moves the slider off 100 (or restarts a share while already off 100).
  const engageComputerAudioGain = useCallback(async () => {
    if (audioMixRef.current) return;
    const room = roomRef.current;
    const pub = room?.localParticipant.getTrackPublication(
      Track.Source.ScreenShareAudio,
    );
    const localTrack = pub?.audioTrack as LocalAudioTrack | undefined;
    if (!localTrack) return;
    try {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      const ctx = new Ctx();
      await ctx.resume().catch(() => undefined);
      const source = ctx.createMediaStreamSource(
        new MediaStream([localTrack.mediaStreamTrack]),
      );
      const gain = ctx.createGain();
      gain.gain.value = computerAudioVolumeRef.current / 100;
      const dest = ctx.createMediaStreamDestination();
      source.connect(gain);
      gain.connect(dest);
      await localTrack.replaceTrack(dest.stream.getAudioTracks()[0]);
      audioMixRef.current = { ctx, gain };
      log('[Room] COMPUTER_AUDIO_GAIN engaged');
    } catch (err) {
      log(`[Room] COMPUTER_AUDIO_GAIN failed: ${String(err)}`);
    }
  }, [log]);

  const teardownComputerAudioGain = useCallback(() => {
    const mix = audioMixRef.current;
    if (!mix) return;
    audioMixRef.current = null;
    void mix.ctx.close().catch(() => undefined);
  }, []);

  const setComputerAudioVolume = useCallback(
    (value: number) => {
      const clamped = Math.max(0, Math.min(200, Math.round(value)));
      setComputerAudioVolumeState(clamped);
      if (audioMixRef.current) {
        audioMixRef.current.gain.gain.value = clamped / 100;
      } else if (clamped !== 100) {
        // First move off unity — splice the gain graph in now.
        void engageComputerAudioGain();
      }
    },
    [engageComputerAudioGain],
  );

  // Ticket 96. Bring the published microphone into line with micGainRef, and
  // record the installation so the next call knows whether the processor is
  // still on the track that is actually being published.
  //
  // Called from three places, and it needs to be all three: the slider, the
  // LocalTrackPublished event (a demote-to-listener-and-back republishes a FRESH
  // mic track with no processor on it), and the connect effect. `setProcessor`
  // handles a device change or a reacquiring unmute by itself, which is the
  // reason for preferring it over replaceTrack — see utils/mic_gain.ts.
  const syncMicGain = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const pub = room.localParticipant.getTrackPublication(
      Track.Source.Microphone,
    );
    const localTrack: MicGainTrack | undefined = pub?.audioTrack;
    const percent = micGainRef.current;
    // Nothing published and nothing installed: no work, and in particular no
    // AudioContext allocated for somebody who is only listening.
    if (!localTrack && !micGainInstallRef.current) return;

    micGainInstallRef.current = await applyMicGain({
      percent,
      track: localTrack,
      current: micGainInstallRef.current,
      createContext: createAudioContext,
      log,
    });

    // Ticket 96 asked for a log, not a cap, when the level gets unreasonable.
    // `applied` is false where the browser has no Web Audio and the boost could
    // not actually be performed — worth distinguishing, or the log overstates
    // how loud the room really got.
    const line = micGainLogLine(
      {
        accountId: myAccountIdRef.current,
        level: percent / 100,
        applied: micGainInstallRef.current !== null,
      },
      micGainLoggedRef.current,
    );
    if (line) log(line);
  }, [log]);

  syncMicGainRef.current = syncMicGain;

  const setMicGain = useCallback(
    (value: number) => {
      const clamped = clampMicGainPercent(value);
      setMicGainState(clamped);
      micGainRef.current = clamped;
      try {
        localStorage.setItem('mastodon_room_mic_gain', String(clamped));
      } catch {
        /* storage unavailable */
      }
      void syncMicGain();
    },
    [syncMicGain],
  );

  // Re-assert the boost whenever a microphone track is published. This is the
  // gap `setProcessor` does not close on its own: it re-routes a track LiveKit
  // RE-ACQUIRES, but a role change unpublishes the track entirely and builds a
  // new one, which arrives with no processor and would publish at 1x while the
  // control still read 300%.
  useEffect(() => {
    const room = roomRef.current;
    if (!roomId || !room) return;
    const onLocalTrackPublished = (pub: { source?: string }) => {
      if (pub.source !== Track.Source.Microphone) return;
      if (micGainRef.current === MIC_GAIN_DEFAULT_PERCENT) return;
      void syncMicGain();
    };
    // Narrowed to the two methods this effect needs, the same way the
    // `isSpeakingChanged` subscription above is: every existing test in this
    // feature hand-rolls a livekit-client mock, and most of those rooms have an
    // `on` but no `off`. Calling `room.off` unguarded here reddened 53 tests
    // across 8 files — a real LiveKit Room is an EventEmitter and always has
    // both, so this costs nothing but the cast.
    const emitter = room as unknown as {
      on?: (event: string, cb: (pub: { source?: string }) => void) => void;
      off?: (event: string, cb: (pub: { source?: string }) => void) => void;
    };
    emitter.on?.(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
    return () => {
      emitter.off?.(RoomEvent.LocalTrackPublished, onLocalTrackPublished);
    };
  }, [roomId, syncMicGain]);

  // Mute/unmute every remote playback element on this machine. The sharer
  // keeps seeing video tiles and chat; only local audio playback is gated.
  // The own `-obs` element is skipped entirely — it must stay muted no matter
  // what (virtual loopback feedback).
  const applyEchoSuppression = useCallback((suppress: boolean) => {
    echoSuppressRef.current = suppress;
    for (const identity of remoteStreamsRef.current.keys()) {
      if (identity === `${myAccountIdRef.current}-obs`) continue;
      for (const suffix of ['', '-screen']) {
        const el = document.getElementById(`lk-audio-${identity}${suffix}`);
        if (el instanceof HTMLMediaElement) el.muted = suppress;
      }
    }
  }, []);

  const echoMuted = echoRisk && !echoMuteOverride;
  useEffect(() => {
    applyEchoSuppression(echoMuted);
  }, [echoMuted, applyEchoSuppression]);

  const toggleEchoMute = useCallback(() => {
    setEchoMuteOverride((v) => !v);
  }, []);

  const toggleScreenShare = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    const next = !isScreenSharing;
    log(`[Room] toggleScreenShare: ${isScreenSharing} -> ${next}`);
    room.localParticipant
      // audio: capture tab/system audio with the share when the browser offers
      // it (livekit defaults to audio: false, which silently streamed video
      // only — viewers never heard e.g. a YouTube video playing).
      // systemAudio 'include' asks Chrome's picker to offer the system-audio
      // checkbox for full-screen shares too.
      .setScreenShareEnabled(next, { audio: true, systemAudio: 'include' })
      .then((pub) => {
        const screenTrack = next
          ? ((pub?.track ??
              room.localParticipant.getTrackPublication(
                Track.Source.ScreenShare,
              )?.track ??
              null) as LocalVideoTrack | null)
          : null;
        setIsScreenSharing(next);
        setLocalScreenTrack(screenTrack);
        if (!next) {
          // Sharing stopped — drop the gain graph (a new share re-captures).
          teardownComputerAudioGain();
          setEchoRisk(false);
          setEchoMuteOverride(false);
        } else {
          if (computerAudioVolumeRef.current !== 100) {
            // Re-sharing while the mix is already off unity — re-engage.
            void engageComputerAudioGain();
          }
          // Echo risk: an audio-bearing share whose surface is not a tab or a
          // window can contain the room's own playback (monitor capture — and
          // "unknown", since Firefox reports no displaySurface). Tab/window
          // audio cannot, so those shares keep room audio audible.
          const audioPub = room.localParticipant.getTrackPublication(
            Track.Source.ScreenShareAudio,
          );
          const hasAudio = !!(audioPub?.track ?? audioPub?.audioTrack);
          const mst = (
            screenTrack as { mediaStreamTrack?: MediaStreamTrack } | null
          )?.mediaStreamTrack;
          const surface =
            typeof mst?.getSettings === 'function'
              ? (mst.getSettings() as { displaySurface?: string })
                  .displaySurface
              : undefined;
          const risky =
            hasAudio && surface !== 'browser' && surface !== 'window';
          setEchoRisk(risky);
          if (risky) {
            log(`[Room] ECHO_RISK share surface=${surface ?? 'unknown'}`);
          }
        }
      })
      .catch((err: unknown) => {
        // The user dismissing the picker rejects with NotAllowedError — normal.
        log(`[Room] toggleScreenShare failed: ${String(err)}`);
      });
  }, [isScreenSharing, log]);

  // Ticket 70: "room disconnects on screen-off". The device auto-locking while
  // someone is listening used to kill the audio until they woke the screen and
  // nudged a control. Two halves, both in `utils/background_audio.ts`:
  //
  //   1. Hold a screen wake lock for as long as we are in the room, so the
  //      phone stops auto-locking mid-conversation. The OS revokes the lock
  //      every time the page hides, so it is re-taken on the way back —
  //      without that it works exactly once per page load.
  //   2. Rebuild playback on return, for the cases the lock cannot prevent (a
  //      deliberate lock, an app switch, a browser with no Wake Lock API).
  //      iOS suspends the playback AudioContext ticket 53 introduced, and
  //      until now the ONLY thing that resumed it was touching the volume
  //      slider again.
  useEffect(() => {
    if (!roomId) return;

    const wakeLock = createWakeLockController({
      requestWakeLock: wakeLockRequester(),
      isPageVisible: () => document.visibilityState === 'visible',
      log,
    });
    void wakeLock.acquire();

    const onVisibilityChange = () => {
      void wakeLock.syncToVisibility();
      if (document.visibilityState !== 'visible') return;

      const room = roomRef.current;
      void recoverBackgroundedAudio({
        contexts: () =>
          Array.from(outputGraphsRef.current.values()).map((g) => g.ctx),
        elements: () => collectRoomAudioElements(document),
        startAudio: room ? () => room.startAudio() : null,
        log,
      })
        .then((report) => {
          // Surface the existing "Tap to enable audio" button when the browser
          // refused the silent replay, rather than leaving a returning viewer
          // in silence with nothing to press.
          if (report.startAudioCalled) setAudioBlocked(false);
          return report;
        })
        .catch(() => undefined);

      // The OS interrupts capture on a backgrounded page, so a speaker can
      // come back apparently unmuted but publishing nothing. Restoring is
      // gated on every intentional reason for silence — see
      // `shouldRestoreMicrophone`.
      if (!room) return;
      const pub = room.localParticipant.getTrackPublication(
        Track.Source.Microphone,
      );
      const restore = shouldRestoreMicrophone({
        canPublish: canPublishRef.current,
        userMuted: isMutedRef.current,
        forceMuted: forceMutedRef.current,
        trackMuted: !pub || pub.isMuted,
      });
      if (!restore) return;
      log('[Room] BACKGROUND_RECOVERY: re-enabling microphone');
      void room.localParticipant.setMicrophoneEnabled(true).catch(() => {
        log('[Room] BACKGROUND_RECOVERY: microphone restore failed');
      });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      void wakeLock.release();
    };
  }, [roomId, log]);

  const cleanup = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    for (const identity of remoteStreamsRef.current.keys()) {
      document.getElementById(`lk-audio-${identity}`)?.remove();
      document.getElementById(`lk-audio-${identity}-screen`)?.remove();
    }
    remoteStreamsRef.current.clear();
    teardownComputerAudioGain();
    // The playback graphs hold an AudioContext each — a real device handle,
    // and iOS allows a page only a handful before refusing to make more.
    releaseGainGraphs(outputGraphsRef.current);
    // Same reasoning for the publish side: the mic gain graph holds a context of
    // its own. The installed processor belongs to a track LiveKit is about to
    // stop, and stopping a track destroys its processor, so only the context is
    // ours to release.
    releaseMicGain(micGainInstallRef.current);
    micGainInstallRef.current = null;
    micGainLoggedRef.current.clear();
    echoSuppressRef.current = false;
    setEchoRisk(false);
    setEchoMuteOverride(false);
    void room.disconnect();
    roomRef.current = null;
  }, [teardownComputerAudioGain]);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  return {
    isMuted,
    forceMuted,
    toggleMute,
    speakingPeers,
    isLocalSpeaking,
    cleanup,
    getLocalStream,
    getRemoteStreams,
    isCameraOn,
    isScreenSharing,
    toggleCamera,
    toggleScreenShare,
    computerAudioVolume,
    setComputerAudioVolume,
    micGain,
    setMicGain,
    echoRisk,
    echoMuted,
    toggleEchoMute,
    audioBlocked,
    resumeAudio,
    masterVolume,
    setMasterVolume,
    localCameraTrack,
    localScreenTrack,
    remoteVideoTracks,
    selfObsPreview,
    setSelfObsPreview,
    logClient: log,
  };
}
